import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Pitch Trainer v1 (Vocal Pitch Training Phase 1) lexicon entries — 20 verdict entries + 4
 * exercise-type entries. Authored by Bowie in the Epic design §12 and locked verbatim.
 *
 * Every entry maps to exactly one `VocalLib` verdict, because this is the first tool in the
 * suite whose output is a *measurement* of the user. A UI that composed its own sentence could
 * assemble a judgement no one wrote and no one reviewed — so it does not compose one, and the
 * only strings a singer reads are these.
 *
 * `{{cents}}` is unsigned integer cents (the direction lives in the entry id, not in the sign);
 * `{{low}}` / `{{high}}` are note names. They interpolate at the use site through
 * `interpolateTokens`, which strips an unfilled token rather than leaking the raw placeholder.
 *
 * The two cross-link entries live in `lexicon-crosslink-entries.ts` with the rest of the
 * cross-tool semantics.
 *
 * Copy rules applied by the author: beginner never uses "cents", "flat/sharp" or "octave" as
 * jargon without a plain gloss; intermediate names the concept and the number; advanced is
 * terse and states the rule. "Wavering" carries no judgement word in any register (§5.6 — this
 * tool has no business telling a singer their vibrato is wrong), and the drift entries lead
 * with the good news in every register because that is §5.5's whole point.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §12
 */
export const VOCAL_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Per-note absolute bands (§5.4) ─────────
  {
    id: 'vocal-on-note',
    category: 'vocal',
    displayName: 'On the note',
    displayNameShort: 'On',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'On the note.',
      intermediate: 'On the note — within 20 cents.',
      advanced: 'On — ≤ 20 ¢.',
    },
    tagLabel: { beginner: 'on', intermediate: 'on', advanced: 'on' },
  },
  {
    id: 'vocal-close-under',
    category: 'vocal',
    displayName: 'A little under',
    displayNameShort: 'Close under',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A little under.',
      intermediate: 'A little flat — {{cents}} cents under.',
      advanced: 'Flat, −{{cents}} ¢.',
    },
    tagLabel: { beginner: 'a bit low', intermediate: 'flat', advanced: '−{{cents}} ¢' },
  },
  {
    id: 'vocal-close-over',
    category: 'vocal',
    displayName: 'A little over',
    displayNameShort: 'Close over',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A little over.',
      intermediate: 'A little sharp — {{cents}} cents over.',
      advanced: 'Sharp, +{{cents}} ¢.',
    },
    tagLabel: { beginner: 'a bit high', intermediate: 'sharp', advanced: '+{{cents}} ¢' },
  },
  {
    id: 'vocal-off-under',
    category: 'vocal',
    displayName: 'Under the note',
    displayNameShort: 'Under',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Under the note.',
      intermediate: 'Flat — {{cents}} cents under.',
      advanced: 'Flat, −{{cents}} ¢.',
    },
    tagLabel: { beginner: 'low', intermediate: 'flat', advanced: '−{{cents}} ¢' },
  },
  {
    id: 'vocal-off-over',
    category: 'vocal',
    displayName: 'Over the note',
    displayNameShort: 'Over',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Over the note.',
      intermediate: 'Sharp — {{cents}} cents over.',
      advanced: 'Sharp, +{{cents}} ¢.',
    },
    tagLabel: { beginner: 'high', intermediate: 'sharp', advanced: '+{{cents}} ¢' },
  },
  {
    id: 'vocal-wrong-note',
    category: 'vocal',
    displayName: 'A different note',
    displayNameShort: 'Wrong note',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'That was a different note.',
      intermediate: 'A different note — past the half-step line.',
      advanced: 'Wrong pitch — > 50 ¢, nearest semitone differs.',
    },
    tagLabel: { beginner: 'different note', intermediate: 'wrong note', advanced: 'wrong note' },
  },
  // ───────── Alignment outcomes (§5.3) ─────────
  {
    id: 'vocal-missed-note',
    category: 'vocal',
    displayName: 'Missed note',
    displayNameShort: 'Missed',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'You skipped a note.',
      intermediate: "One reference note wasn't sung.",
      advanced: 'Deletion — reference note unmatched.',
    },
    tagLabel: { beginner: 'skipped', intermediate: 'missed', advanced: 'missed' },
  },
  {
    id: 'vocal-extra-note',
    category: 'vocal',
    displayName: 'Extra note',
    displayNameShort: 'Extra',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'You added a note.',
      intermediate: "One sung note isn't in the phrase.",
      advanced: 'Insertion — sung note unmatched.',
    },
    tagLabel: { beginner: 'added', intermediate: 'extra', advanced: 'extra' },
  },
  // ───────── Stability, reported and never scored (§5.6) ─────────
  {
    id: 'vocal-steady',
    category: 'vocal',
    displayName: 'Steady',
    displayNameShort: 'Steady',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Steady.',
      intermediate: 'Steady — the note held its centre.',
      advanced: 'Steady — IQR ≤ 90 ¢.',
    },
    tagLabel: { beginner: 'steady', intermediate: 'steady', advanced: 'steady' },
  },
  {
    id: 'vocal-wavering',
    category: 'vocal',
    displayName: 'Wavering',
    displayNameShort: 'Wavering',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Wandering a bit.',
      intermediate: 'Wavering — the pitch moved more than vibrato would.',
      advanced: 'Wavering — IQR > 90 ¢; not scored.',
    },
    tagLabel: { beginner: 'wandering', intermediate: 'wavering', advanced: 'wavering' },
  },
  // ───────── The octave rule (§5.7) ─────────
  {
    id: 'vocal-octave-under',
    category: 'vocal',
    displayName: 'Sung an octave lower',
    displayNameShort: 'Octave down',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You sang it an octave lower — that's fine.",
      intermediate: 'Sung an octave down; scored at your octave.',
      advanced: '−12 transposition applied; scored on transposed pitches.',
    },
    tagLabel: { beginner: 'octave down', intermediate: '8vb', advanced: '8vb' },
  },
  {
    id: 'vocal-octave-over',
    category: 'vocal',
    displayName: 'Sung an octave higher',
    displayNameShort: 'Octave up',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You sang it an octave higher — that's fine.",
      intermediate: 'Sung an octave up; scored at your octave.',
      advanced: '+12 transposition applied; scored on transposed pitches.',
    },
    tagLabel: { beginner: 'octave up', intermediate: '8va', advanced: '8va' },
  },
  // ───────── The §5.5 headline: good relative pitch, drifting absolute ─────────
  {
    id: 'vocal-good-relative-drift-under',
    category: 'vocal',
    displayName: 'Intervals right, drifted flat',
    displayNameShort: 'Drifted flat',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Your intervals were right — the whole thing settled a little low.',
      intermediate: 'Intervals correct; the phrase drifted {{cents}} cents flat overall. Support, not ear.',
      advanced: 'Relative pitch intact; mean offset −{{cents}} ¢.',
    },
    tagLabel: { beginner: 'drifted low', intermediate: 'drifted flat', advanced: '−{{cents}} ¢ drift' },
  },
  {
    id: 'vocal-good-relative-drift-over',
    category: 'vocal',
    displayName: 'Intervals right, drifted sharp',
    displayNameShort: 'Drifted sharp',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Your intervals were right — the whole thing crept a little high.',
      intermediate: 'Intervals correct; the phrase drifted {{cents}} cents sharp overall.',
      advanced: 'Relative pitch intact; mean offset +{{cents}} ¢.',
    },
    tagLabel: { beginner: 'drifted high', intermediate: 'drifted sharp', advanced: '+{{cents}} ¢ drift' },
  },
  // ───────── Silence is a first-class outcome (§9) ─────────
  {
    id: 'vocal-nothing-heard',
    category: 'vocal',
    displayName: 'Nothing heard',
    displayNameShort: 'No signal',
    pedagogicalLevel: 1,
    copy: {
      beginner: "We didn't hear anything — check your microphone.",
      intermediate: 'Nothing voiced was detected. Check the mic, and sing a little louder.',
      advanced: 'No frames passed the voicing gate (clarity ≥ 0.9, +12 dB over floor).',
    },
    tagLabel: { beginner: 'no signal', intermediate: 'nothing heard', advanced: 'gate closed' },
  },
  {
    id: 'vocal-no-signal',
    category: 'vocal',
    displayName: 'No signal',
    displayNameShort: 'No signal',
    pedagogicalLevel: 1,
    copy: {
      beginner: "We're not getting any sound from your microphone — check it's the right one and isn't muted.",
      intermediate: "No signal from the microphone. Check the input device and that it isn't muted.",
      advanced: 'Peak below −60 dBFS — no signal. Wrong device, muted, or permission.',
    },
    tagLabel: { beginner: 'mic', intermediate: 'no signal', advanced: 'no signal' },
  },
  {
    id: 'vocal-too-quiet',
    category: 'vocal',
    displayName: 'Too quiet',
    displayNameShort: 'Too quiet',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'We heard you, but too quietly — sing out, or move closer to the mic.',
      intermediate:
        'Too quiet — the sound never rose 12 dB above the room noise. Sing out or move closer.',
      advanced: 'Signal never cleared the RMS gate (floor + 12 dB). Level, distance, or room noise.',
    },
    tagLabel: { beginner: 'too quiet', intermediate: 'too quiet', advanced: 'below gate' },
  },
  {
    id: 'vocal-unclear',
    category: 'vocal',
    displayName: 'No clear pitch',
    displayNameShort: 'Unclear',
    pedagogicalLevel: 1,
    copy: {
      beginner: "We heard sound but couldn't find a pitch — try a clear 'ah' with less breath, or a quieter room.",
      intermediate:
        "Sound arrived but no clear pitch — a steady 'ah' without breathiness, or less background noise.",
      advanced:
        'Clarity never reached the voicing threshold. Breathy tone, noise, or the threshold is too strict for this voice.',
    },
    tagLabel: { beginner: 'no pitch', intermediate: 'unclear', advanced: 'low clarity' },
  },
  // ───────── Range calibration (§5.8) ─────────
  {
    id: 'vocal-range-calibrated',
    category: 'vocal',
    displayName: 'Range saved',
    displayNameShort: 'Range set',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Range saved: {{low}} to {{high}}.',
      intermediate: 'Range set — exercises will sit in the middle of it.',
      advanced: 'Range {{low}}–{{high}}; exercises transposed into the middle 60 %.',
    },
    tagLabel: { beginner: 'range set', intermediate: 'calibrated', advanced: 'calibrated' },
  },
  {
    id: 'vocal-calibrate-first',
    category: 'vocal',
    displayName: 'Calibrate your range',
    displayNameShort: 'Calibrate',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Let's find your range first — sing your lowest comfortable note, then your highest.",
      intermediate: 'Calibrate your range so exercises land where you can sing them.',
      advanced: 'Range uncalibrated; exercises need a tessitura target.',
    },
    tagLabel: { beginner: 'find your range', intermediate: 'calibrate', advanced: 'uncalibrated' },
  },
  // ───────── Setup advice (§9) ─────────
  {
    id: 'vocal-headphones',
    category: 'vocal',
    displayName: 'Headphones recommended',
    displayNameShort: 'Headphones',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Headphones help — the speaker can leak into the mic.',
      intermediate: 'Use headphones; playback is muted while you sing, but speaker bleed still hurts.',
      advanced: 'Headphones recommended; capture is playback-muted.',
    },
    tagLabel: { beginner: 'headphones', intermediate: 'headphones', advanced: 'headphones' },
  },
  {
    id: 'vocal-privacy',
    category: 'vocal',
    displayName: 'Your voice stays here',
    displayNameShort: 'On-device',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Your voice stays on this device — nothing is recorded or sent.',
      intermediate: 'Analysed in the browser; no audio is stored or uploaded.',
      advanced: 'In-browser analysis only; derived numbers persist, audio does not.',
    },
    tagLabel: { beginner: 'private', intermediate: 'on-device', advanced: 'on-device' },
  },
  {
    id: 'vocal-intro',
    category: 'vocal',
    displayName: 'Pitch Trainer',
    displayNameShort: 'Sing back',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Hear it, then sing it back. We'll tell you how close you got.",
      intermediate:
        'Sing back a note, an interval or a short phrase; get pitch feedback on each note and on the shape.',
      advanced: 'Reproduction ear training: absolute and relative pitch scored per phrase.',
    },
    tagLabel: { beginner: 'sing back', intermediate: 'sing back', advanced: 'sing back' },
  },
  // ───────── The four exercise types (§6) ─────────
  {
    id: 'vocal-ex-match-tone',
    category: 'vocal',
    displayName: 'Match a tone',
    displayNameShort: 'Match a tone',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Hold one note.',
      intermediate: 'Match a single tone and hold it for two seconds.',
      advanced: 'Sustain: one pitch, 2 s, scored on the post-scoop median.',
    },
    tagLabel: { beginner: 'Match a tone', intermediate: 'Match a tone', advanced: 'Match a tone' },
  },
  {
    id: 'vocal-ex-interval',
    category: 'vocal',
    displayName: 'Sing an interval',
    displayNameShort: 'Sing an interval',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Sing the first note, then the second.',
      intermediate: 'Sing the root, then the interval above it.',
      advanced: 'Ascending melodic interval; both notes scored, plus the interval between them.',
    },
    tagLabel: { beginner: 'Sing an interval', intermediate: 'Sing an interval', advanced: 'Sing an interval' },
  },
  {
    id: 'vocal-ex-vocalise',
    category: 'vocal',
    displayName: 'Vocalise',
    displayNameShort: 'Vocalise',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A little warm-up pattern.',
      intermediate: 'A vocalise — 1-2-3-4-5-4-3-2-1, 1-3-5-3-1, 1-3-5-8-5-3-1.',
      advanced: 'Stepwise and arpeggiated vocalises; major then natural minor.',
    },
    tagLabel: { beginner: 'Vocalise', intermediate: 'Vocalise', advanced: 'Vocalise' },
  },
  {
    id: 'vocal-ex-phrase',
    category: 'vocal',
    displayName: 'Short phrase',
    displayNameShort: 'Short phrase',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A short tune to sing back.',
      intermediate: 'A short diatonic phrase, 3–5 notes, ending on the tonic or the third.',
      advanced: 'Stepwise-dominant diatonic phrase; closes on 1̂ or 3̂.',
    },
    tagLabel: { beginner: 'Short phrase', intermediate: 'Short phrase', advanced: 'Short phrase' },
  },
]
